
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  commitTicketImportJob,
  createTicketImportJob,
  downloadTicketImportTemplate,
  previewTicketImportJob,
  rollbackTicketImportJob,
} from "../../api/ticketImport";
import { useAuth } from "../../context/AuthContext";
import { getCurrentSalonId, getCurrentSalonName } from "../../utils/currentSalon";
import {
  MODAL_TEXT,
  LABEL_TEXT,
  FIELD_CLASS,
  BTN_PRIMARY,
  BTN_OUTLINE,
  BTN_DANGER,
} from "../../ui/modalStyles";
import SBINModal from "../modals/SBINModal";
import InfoTooltip from "../common/InfoTooltip";
import ReportHelpCard from "../reports/ReportHelpCard";
import { formatDateOnlyYYYYMMDD } from "../../utils/dateOnly";

const STEP_LABELS = [
  "1. Upload",
  "2. Column Mapping",
  "3. Preview",
  "4. Commit",
];

const FIELD_DEFS = [
  { key: "external_ticket_number", label: "External Ticket # (required)" },
  { key: "ticket_date", label: "Ticket Date (required) — YYYY-MM-DD" },
  { key: "opened_at", label: "Opened At (optional)" },
  { key: "closed_at", label: "Closed At (optional)" },
  { key: "status", label: "Status" },
  { key: "ticket_type", label: "Ticket Type" },
  { key: "ticket_notes", label: "Ticket Notes" },
  { key: "is_tax_exempt", label: "Tax Exempt (Yes/No)" },
  { key: "client_full_name", label: "Client Name" },
  { key: "client_phone", label: "Client Phone" },
  { key: "client_email", label: "Client Email" },
  { key: "service_name", label: "Service Name" },
  { key: "item_description", label: "Item Description (required)" },
  { key: "quantity", label: "Quantity" },
  { key: "unit_price", label: "Unit Price" },
  { key: "discount_type", label: "Discount Type" },
  { key: "discount_value", label: "Discount Value" },
  { key: "line_discount", label: "Line Discount" },
  { key: "tax_rate", label: "Tax Rate" },
  { key: "tax_amount", label: "Tax Amount" },
  { key: "line_total", label: "Line Total" },
  { key: "service_base_price", label: "Service Base Price" },
  { key: "service_duration_minutes", label: "Service Duration Minutes" },
  { key: "service_description", label: "Service Description" },
  { key: "staff_name", label: "Staff Name" },
  { key: "staff_phone", label: "Staff Phone" },
  { key: "staff_email", label: "Staff Email" },
  { key: "payment_method", label: "Payment Method" },
  { key: "payment_amount", label: "Payment Amount" },
  { key: "tip_amount", label: "Tip Amount" },
  { key: "payment_reference", label: "Payment Reference" },
  { key: "payment_note", label: "Payment Note" },
];

const SYNONYMS = {
  external_ticket_number: [
    "external ticket number",
    "ticket number",
    "external id",
    "external_ticket_number",
  ],
  ticket_date: ["ticket date", "date", "service date", "visit date", "ticket_date"],
  opened_at: ["opened at", "opened", "open date", "created at"],
  closed_at: ["closed at", "closed", "close date"],
  status: ["status", "ticket status"],
  ticket_type: ["ticket type", "type"],
  ticket_notes: ["ticket notes", "notes", "memo"],
  is_tax_exempt: ["tax exempt", "is tax exempt", "tax exemption"],
  client_full_name: ["client name", "full name", "customer name"],
  client_phone: ["client phone", "phone", "customer phone", "mobile"],
  client_email: ["client email", "email", "customer email"],
  service_name: ["service", "service name"],
  staff_name: ["staff", "staff name", "technician"],
  item_description: ["item description", "description", "item", "service description"],
  quantity: ["qty", "quantity", "units"],
  unit_price: ["unit price", "price", "unit cost"],
  discount_type: ["discount type"],
  discount_value: ["discount value", "discount"],
  line_discount: ["line discount", "discount amount"],
  tax_rate: ["tax rate", "tax %"],
  tax_amount: ["tax amount"],
  line_total: ["line total", "total"],
  service_base_price: ["service base price", "base price", "service price"],
  service_duration_minutes: [
    "service duration minutes",
    "service duration",
    "duration minutes",
    "duration",
  ],
  service_description: ["service description", "service details"],
  staff_phone: ["staff phone", "technician phone", "employee phone"],
  staff_email: ["staff email", "technician email", "employee email"],
  payment_method: ["payment method", "method", "payment type"],
  payment_amount: ["payment amount", "amount paid", "payment"],
  tip_amount: ["tip amount", "tip"],
  payment_reference: ["payment reference", "reference"],
  payment_note: ["payment note", "payment notes", "note"],
};

const ISSUE_LABELS = {
  EXTERNAL_TICKET_NUMBER_REQUIRED: "External ticket # required",
  TICKET_DATE_REQUIRED: "Ticket date required",
  TICKET_DATE_INVALID: "Ticket date invalid",
  OPENED_AT_INVALID: "Opened at invalid",
  CLOSED_AT_INVALID: "Closed at invalid",
  ITEM_DESCRIPTION_REQUIRED: "Item description required",
  TAX_EXEMPT_INVALID: "Tax exempt invalid",
  QUANTITY_INVALID: "Quantity invalid",
  UNIT_PRICE_INVALID: "Unit price invalid",
  DISCOUNT_VALUE_INVALID: "Discount value invalid",
  LINE_DISCOUNT_INVALID: "Line discount invalid",
  TAX_RATE_INVALID: "Tax rate invalid",
  TAX_AMOUNT_INVALID: "Tax amount invalid",
  LINE_TOTAL_INVALID: "Line total invalid",
  PAYMENT_AMOUNT_INVALID: "Payment amount invalid",
  TIP_AMOUNT_INVALID: "Tip amount invalid",
  PAYMENT_METHOD_MISSING: "Payment method missing",
  SERVICE_NOT_FOUND: "Service not found",
  STAFF_NOT_FOUND: "Staff not found",
  GROUP_CONFLICT: "Group conflict",
  GROUP_HAS_ERRORS: "Group has errors",
  DUPLICATE_IN_SYSTEM: "Duplicate in system",
  NOT_SAFE_TO_UPDATE: "Not safe to update",
  CLIENT_REQUIRED: "Client required",
  CLIENT_NAME_REQUIRED: "Client name required",
  UPDATE_NOT_ALLOWED: "Update not allowed",
  TICKET_NOT_FOUND: "Ticket not found",
};

const ACTION_STYLES = {
  CREATE: "bg-emerald-100 text-emerald-700",
  UPDATE: "bg-blue-100 text-blue-700",
  SKIP: "bg-slate-100 text-slate-600",
  REVIEW: "bg-amber-100 text-amber-700",
  ERROR: "bg-rose-100 text-rose-700",
};

const HELP_SECTIONS = [
  {
    id: "upload",
    title: "Step 1: Upload file",
    description: "Upload your ticket file, confirm options, and continue to mapping.",
    anchorId: "ti-step-1",
    bullets: [
      "Use CSV or XLSX format.",
      "Confirm salon context before import.",
      "Set create/update options for clients, services, and staff.",
    ],
    tips: [
      "Start with a small test file first.",
      "Keep ticket dates in YYYY-MM-DD format.",
    ],
  },
  {
    id: "mapping",
    title: "Step 2: Column mapping",
    description: "Map source columns to ticket import fields.",
    anchorId: "ti-step-2",
    bullets: [
      "External Ticket # and Ticket Date are required.",
      "Map Item Description or Service Name.",
      "Leave optional fields unmapped if not available.",
    ],
    tips: ["Use template headers for better auto-mapping."],
  },
  {
    id: "preview",
    title: "Step 3: Preview and fix",
    description: "Review group actions, issues, and generated service/staff records.",
    anchorId: "ti-step-3",
    bullets: [
      "Check creates, updates, skips, and errors.",
      "Set invalid groups to SKIP if needed.",
      "Review new services/staff before commit.",
    ],
    tips: ["Resolve all blocking errors before continuing."],
  },
  {
    id: "commit",
    title: "Step 4: Commit import",
    description: "Commit changes, review results, and rollback if needed.",
    anchorId: "ti-step-4",
    bullets: [
      "Commit writes ticket changes to your salon.",
      "Review final counts for created, updated, skipped, failed.",
      "Use Undo Import only for immediate rollback.",
    ],
    tips: ["Open Import History after commit for audit checks."],
  },
];

const TROUBLESHOOTING_ITEMS = [
  {
    title: "Upload fails",
    body: "Check file format, required fields, and salon context.",
  },
  {
    title: "Preview shows errors",
    body: "Fix mappings or set problematic groups to Skip.",
  },
  {
    title: "Commit blocked",
    body: "Resolve preview errors before moving to commit.",
  },
];

const TOOLTIPS = {
  upload_file: "Upload CSV or XLSX containing ticket rows.",
  salon: "Import applies to this salon only.",
  create_missing_clients: "Create client records if they are not found.",
  create_missing_services: "Create missing services from imported lines.",
  create_missing_staff: "Create missing staff profiles from ticket history.",
  infer_service_base_price: "Use historical ticket prices as service base price.",
  allow_updates: "Allow matching existing tickets to be updated.",
  default_service_duration: "Used when creating new services with no duration.",
  mapping: "Map each source column to the correct ticket field.",
  action: "Choose create, update, or skip per ticket group.",
  issues: "Errors and warnings found during validation.",
  commit: "Final step that writes import changes.",
};

function normalizeHeader(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function autoMapHeaders(headers = []) {
  const normalized = headers.map((header) => ({
    raw: header,
    norm: normalizeHeader(header),
  }));
  const used = new Set();
  const mapping = {};

  FIELD_DEFS.forEach((field) => {
    const synonyms = SYNONYMS[field.key] || [];
    const match = normalized.find(
      (header) =>
        !used.has(header.raw) &&
        (synonyms.includes(header.norm) || header.norm === normalizeHeader(field.key))
    );
    if (match) {
      mapping[field.key] = match.raw;
      used.add(match.raw);
    } else {
      mapping[field.key] = "";
    }
  });
  return mapping;
}

function buildPageRange(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = Array.from(pages)
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);
  const output = [];
  let last = 0;
  for (const page of sorted) {
    if (page - last > 1) {
      output.push("ellipsis");
    }
    output.push(page);
    last = page;
  }
  return output;
}

function paginateRows(rows, page, perPage) {
  const total = rows.length;
  const startIndex = (page - 1) * perPage;
  const endIndex = Math.min(startIndex + perPage, total);
  return {
    rows: rows.slice(startIndex, endIndex),
    total,
    startIndex: total === 0 ? 0 : startIndex + 1,
    endIndex,
  };
}

function issueCode(issue) {
  if (!issue) return "UNKNOWN";
  return typeof issue === "string" ? issue : issue.code || "UNKNOWN";
}

export default function TicketImportWizard({
  embedded = false,
  initialSalonId = null,
  onComplete,
  onClose,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [selectedSalonId, setSelectedSalonId] = useState(null);
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [jobId, setJobId] = useState("");
  const [mapping, setMapping] = useState({});
  const [allowUpdates, setAllowUpdates] = useState(false);
  const [createMissingClients, setCreateMissingClients] = useState(true);
  const [createMissingServices, setCreateMissingServices] = useState(true);
  const [createMissingStaff, setCreateMissingStaff] = useState(true);
  const [inferServiceBasePrice, setInferServiceBasePrice] = useState(true);
  const [defaultServiceDurationMinutes, setDefaultServiceDurationMinutes] =
    useState(30);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [commitLoading, setCommitLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewPage, setPreviewPage] = useState(1);
  const [groupOverrides, setGroupOverrides] = useState({});
  const [serviceOverridesByTempKey, setServiceOverridesByTempKey] = useState({});
  const [staffOverridesByTempKey, setStaffOverridesByTempKey] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [commitResult, setCommitResult] = useState(null);
  const [rollbackModalOpen, setRollbackModalOpen] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [rollbackReport, setRollbackReport] = useState(null);
  const [highlightedSectionId, setHighlightedSectionId] = useState("");
  const highlightTimeoutRef = useRef(null);

  const currentSalonId = getCurrentSalonId(user);
  const salonName = getCurrentSalonName(user);
  const hideSalonSelector = true;
  const resolvedSalonId = initialSalonId ?? currentSalonId;
  const isSalonLocked = hideSalonSelector || Boolean(initialSalonId);

  useEffect(() => {
    if (!resolvedSalonId) return;
    setSelectedSalonId(resolvedSalonId);
    setError((prev) => {
      if (
        prev === "Select a salon before importing tickets." ||
        prev === "No salon context found. Please log in again."
      ) {
        return "";
      }
      return prev;
    });
  }, [resolvedSalonId]);

  useEffect(() => {
    if (selectedSalonId || resolvedSalonId) return;
    if (isSalonLocked) {
      setError("No salon context found. Please log in again.");
    } else {
      setError("Select a salon before importing tickets.");
    }
    const timeout = setTimeout(() => {
      navigate("/my-salons", { replace: true });
    }, 600);
    return () => clearTimeout(timeout);
  }, [selectedSalonId, resolvedSalonId, isSalonLocked, navigate]);

  useEffect(
    () => () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    },
    []
  );

  const optionsPayload = useMemo(
    () => ({
      allow_updates: allowUpdates,
      create_missing_clients: createMissingClients,
      create_missing_services: createMissingServices,
      create_missing_staff: createMissingStaff,
      infer_service_base_price: inferServiceBasePrice,
      default_service_duration_minutes: Number(defaultServiceDurationMinutes) || 30,
    }),
    [
      allowUpdates,
      createMissingClients,
      createMissingServices,
      createMissingStaff,
      inferServiceBasePrice,
      defaultServiceDurationMinutes,
    ]
  );

  const previewGroups = useMemo(() => preview?.groups || [], [preview]);
  const summaryCounts = useMemo(() => {
    const summary = preview?.summary || {};
    return {
      creates: summary.create ?? summary.creates ?? 0,
      updates: summary.update ?? summary.updates ?? 0,
      skips: summary.skip ?? summary.skips ?? 0,
      errors: summary.error ?? summary.errors ?? 0,
    };
  }, [preview]);

  useEffect(() => {
    if (!preview) {
      setServiceOverridesByTempKey({});
      setStaffOverridesByTempKey({});
      return;
    }
    const nextServices = {};
    (preview.new_services || []).forEach((service) => {
      nextServices[service.temp_key] = {
        action: "CREATE",
        ...service,
      };
    });
    const nextStaff = {};
    (preview.new_staff || []).forEach((staff) => {
      nextStaff[staff.temp_key] = {
        action: "CREATE",
        ...staff,
      };
    });
    setServiceOverridesByTempKey(nextServices);
    setStaffOverridesByTempKey(nextStaff);
  }, [preview]);

  const pageInfo = useMemo(
    () => paginateRows(previewGroups, previewPage, 15),
    [previewGroups, previewPage]
  );

  const mappingErrors = useMemo(() => {
    const errors = [];
    const hasExternal = mapping.external_ticket_number && mapping.external_ticket_number.trim();
    const hasTicketDate = mapping.ticket_date && mapping.ticket_date.trim();
    const hasItem = mapping.item_description && mapping.item_description.trim();
    const hasService = mapping.service_name && mapping.service_name.trim();
    if (!hasExternal) errors.push("Map External Ticket #");
    if (!hasTicketDate) errors.push("Map Ticket Date");
    if (!(hasItem || hasService)) errors.push("Map Item Description or Service Name");
    return errors;
  }, [mapping]);

  const mappingIsValid = mappingErrors.length === 0;

  const unresolvedPreviewErrors = useMemo(() => {
    return previewGroups.some((group) => {
      const errors = group.errors || [];
      const groupKey = group.group_key || group.group_id;
      const override = groupOverrides[groupKey];
      const action =
        (override?.action || group.proposed_action || "CREATE").toUpperCase();
      if (errors.length === 0) return false;
      if (action === "SKIP") return false;
      return true;
    });
  }, [previewGroups, groupOverrides]);

  const scrollToSection = useCallback((id) => {
    const target = typeof document !== "undefined" ? document.getElementById(id) : null;
    if (!target) return false;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    return true;
  }, []);

  const jumpToSectionWithHighlight = useCallback(
    (id) => {
      const stepByAnchor = {
        "ti-step-1": 1,
        "ti-step-2": 2,
        "ti-step-3": 3,
        "ti-step-4": 4,
      };
      const targetStep = stepByAnchor[id];
      const runHighlight = () => {
        const jumped = scrollToSection(id);
        if (!jumped) return;
        setHighlightedSectionId(id);
        if (highlightTimeoutRef.current) {
          clearTimeout(highlightTimeoutRef.current);
        }
        highlightTimeoutRef.current = setTimeout(() => {
          setHighlightedSectionId((current) => (current === id ? "" : current));
        }, 1500);
      };

      if (targetStep && targetStep !== step) {
        setStep(targetStep);
        setTimeout(runHighlight, 120);
        return;
      }
      runHighlight();
    },
    [scrollToSection, step]
  );

  const getSectionClassName = useCallback(
    (id, baseClassName) =>
      `${baseClassName} ${
        highlightedSectionId === id
          ? "ring-2 ring-rose-300 ring-offset-2 transition-shadow duration-200"
          : ""
      }`,
    [highlightedSectionId]
  );

  const handleDownloadTemplate = async () => {
    setError("");
    try {
      const blob = await downloadTicketImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "ticket_import_template.csv";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError("Unable to download template.");
    }
  };

  const handleUpload = async () => {
    if (!file || !selectedSalonId) return;
    setLoading(true);
    setError("");
    try {
      const data = await createTicketImportJob({
        file,
        salon_id: selectedSalonId,
      });
      setJobId(data.job_id);
      const nextHeaders = Array.isArray(data.headers) ? data.headers : [];
      setHeaders(nextHeaders);
      const autoMapping = autoMapHeaders(nextHeaders);
      setMapping(autoMapping);
      setPreview(null);
      setGroupOverrides({});
      setExpandedGroups({});
      setServiceOverridesByTempKey({});
      setStaffOverridesByTempKey({});
      setCommitResult(null);
      setRollbackReport(null);
      setPreviewPage(1);
      setStep(2);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || "Unable to upload file."
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!mappingIsValid || !jobId) return;
    setPreviewLoading(true);
    setError("");
    try {
      const data = await previewTicketImportJob(jobId, {
        mapping,
        options: optionsPayload,
      });
      setPreview(data);
      setGroupOverrides({});
      setExpandedGroups({});
      setPreviewPage(1);
      setStep(3);
      setRollbackReport(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || "Unable to preview import."
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCommit = async () => {
    if (!jobId) return;
    setCommitLoading(true);
    setError("");
    try {
      const group_overrides = Object.entries(groupOverrides).map(
        ([groupKey, data]) => ({
          group_key: groupKey,
          action: data.action,
        })
      );
      const service_overrides = Object.entries(serviceOverridesByTempKey).map(
        ([tempKey, data]) => ({
          temp_key: tempKey,
          action: data.action,
          name: data.name,
          base_price:
            data.base_price === "" || data.base_price === null
              ? null
              : Number(data.base_price),
          duration_minutes:
            data.duration_minutes === "" || data.duration_minutes === null
              ? null
              : Number(data.duration_minutes),
          description: data.description || "",
        })
      );
      const staff_overrides = Object.entries(staffOverridesByTempKey).map(
        ([tempKey, data]) => ({
          temp_key: tempKey,
          action: data.action,
          display_name: data.display_name,
          phone: data.phone || "",
          email: data.email || "",
        })
      );
      const data = await commitTicketImportJob(jobId, {
        options: optionsPayload,
        group_overrides,
        service_overrides,
        staff_overrides,
      });
      setCommitResult(data);
      setRollbackReport(null);
      if (typeof onComplete === "function") {
        onComplete(data);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || "Unable to commit import."
      );
    } finally {
      setCommitLoading(false);
    }
  };

  const handleOverrideAction = (groupId, action) => {
    setGroupOverrides((prev) => ({
      ...prev,
      [groupId]: {
        ...prev[groupId],
        action,
      },
    }));
  };

  const handleServiceOverrideChange = (tempKey, field, value) => {
    setServiceOverridesByTempKey((prev) => ({
      ...prev,
      [tempKey]: {
        ...prev[tempKey],
        [field]: value,
      },
    }));
  };

  const handleStaffOverrideChange = (tempKey, field, value) => {
    setStaffOverridesByTempKey((prev) => ({
      ...prev,
      [tempKey]: {
        ...prev[tempKey],
        [field]: value,
      },
    }));
  };

  const handleRollback = async () => {
    if (!commitResult?.job_id) return;
    setRollbackLoading(true);
    setError("");
    try {
      const data = await rollbackTicketImportJob(commitResult.job_id);
      setRollbackReport(data);
      setRollbackModalOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || "Unable to roll back this import."
      );
    } finally {
      setRollbackLoading(false);
    }
  };

  const renderPagination = (page, totalRows, setPage) => {
    const totalPages = Math.max(1, Math.ceil(totalRows / 15));
    const pages = buildPageRange(page, totalPages);
    return (
      <div className="flex items-center gap-1 text-sm">
        <button
          type="button"
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="h-10 w-10 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white"
        >
          {"<"}
        </button>
        {pages.map((entry, idx) =>
          entry === "ellipsis" ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-slate-400">
              ...
            </span>
          ) : (
            <button
              key={entry}
              type="button"
              onClick={() => setPage(entry)}
              className={`h-10 w-10 rounded-full border text-sm md:text-base ${
                entry === page
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {entry}
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => setPage(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className="h-10 w-10 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white"
        >
          {">"}
        </button>
      </div>
    );
  };

  const stepHeader = (
    <div className="flex flex-wrap gap-2">
      {STEP_LABELS.map((label, idx) => {
        const isActive = step === idx + 1;
        return (
          <span
            key={label}
            className={`h-9 px-4 rounded-full text-sm md:text-base font-semibold flex items-center ${
              isActive
                ? "bg-slate-900 text-white"
                : "border border-slate-200 text-slate-600"
            }`}
          >
            {label}
          </span>
        );
      })}
    </div>
  );

  const renderStepOne = (
    <div id="ti-step-1" className={getSectionClassName("ti-step-1", "space-y-5")}>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleDownloadTemplate}
          className={BTN_OUTLINE}
        >
          Download CSV template
        </button>
        <div className="text-sm md:text-base text-slate-500">
          Upload CSV or XLSX with your ticket data.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className={`inline-flex items-center gap-2 ${LABEL_TEXT}`}>
            Upload File
            <InfoTooltip text={TOOLTIPS.upload_file} label="Upload File" />
          </div>
          <input
            type="file"
            accept=".csv,.xlsx"
            onChange={(event) => setFile(event.target.files?.[0] || null)}
            className={`mt-1 w-full ${FIELD_CLASS}`}
          />
        </div>
        <div>
          <div className={`inline-flex items-center gap-2 ${LABEL_TEXT}`}>
            Salon
            <InfoTooltip text={TOOLTIPS.salon} label="Salon" />
          </div>
          <div className="mt-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm md:text-base text-slate-700">
            Salon: {salonName || "Unknown salon"}
            {selectedSalonId ? ` (ID ${selectedSalonId})` : ""}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="flex items-center gap-3 text-sm md:text-base text-slate-700">
          <input
            type="checkbox"
            checked={createMissingClients}
            onChange={(event) => setCreateMissingClients(event.target.checked)}
          />
          Create missing clients
          <InfoTooltip
            text={TOOLTIPS.create_missing_clients}
            label="Create missing clients"
          />
        </label>
        <label className="flex items-center gap-3 text-sm md:text-base text-slate-700">
          <input
            type="checkbox"
            checked={createMissingServices}
            onChange={(event) => setCreateMissingServices(event.target.checked)}
          />
          Create missing services
          <InfoTooltip
            text={TOOLTIPS.create_missing_services}
            label="Create missing services"
          />
        </label>
        <label className="flex items-center gap-3 text-sm md:text-base text-slate-700">
          <input
            type="checkbox"
            checked={createMissingStaff}
            onChange={(event) => setCreateMissingStaff(event.target.checked)}
          />
          Create missing staff
          <InfoTooltip
            text={TOOLTIPS.create_missing_staff}
            label="Create missing staff"
          />
        </label>
        <label className="flex items-center gap-3 text-sm md:text-base text-slate-700">
          <input
            type="checkbox"
            checked={inferServiceBasePrice}
            onChange={(event) => setInferServiceBasePrice(event.target.checked)}
          />
          Infer service base price from ticket history
          <InfoTooltip
            text={TOOLTIPS.infer_service_base_price}
            label="Infer service base price"
          />
        </label>
        <label className="flex items-center gap-3 text-sm md:text-base text-slate-700">
          <input
            type="checkbox"
            checked={allowUpdates}
            onChange={(event) => setAllowUpdates(event.target.checked)}
          />
          Allow updating existing tickets
          <InfoTooltip text={TOOLTIPS.allow_updates} label="Allow updates" />
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className={`inline-flex items-center gap-2 ${LABEL_TEXT}`}>
            Default service duration (minutes)
            <InfoTooltip
              text={TOOLTIPS.default_service_duration}
              label="Default service duration"
            />
          </div>
          <input
            type="number"
            min="0"
            value={defaultServiceDurationMinutes}
            onChange={(event) =>
              setDefaultServiceDurationMinutes(event.target.value)
            }
            className={`mt-1 w-full ${FIELD_CLASS}`}
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleUpload}
          className={BTN_PRIMARY}
          disabled={loading || !file || !selectedSalonId}
        >
          {loading ? "Uploading..." : "Continue"}
        </button>
      </div>
    </div>
  );

  const renderStepTwo = (
    <div id="ti-step-2" className={getSectionClassName("ti-step-2", "space-y-5")}>
      <div className="text-sm md:text-base text-slate-600">
        Detected {headers.length} columns from the file. Map them to ticket fields.
      </div>
      <div className="inline-flex items-center gap-2 text-sm md:text-base text-slate-600">
        Column mapping
        <InfoTooltip text={TOOLTIPS.mapping} label="Column Mapping" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {FIELD_DEFS.map((field) => (
          <div key={field.key}>
            <div className={LABEL_TEXT}>{field.label}</div>
            <select
              value={mapping[field.key] || ""}
              onChange={(event) =>
                setMapping((prev) => ({
                  ...prev,
                  [field.key]: event.target.value,
                }))
              }
              className={`mt-1 w-full ${FIELD_CLASS}`}
            >
              <option value="">Not mapped</option>
              {headers.map((header) => (
                <option key={`${field.key}-${header}`} value={header}>
                  {header}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {mappingErrors.length > 0 && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
          {`Please resolve: ${mappingErrors.join(", ")}.`}
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setStep(1)}
          className={BTN_OUTLINE}
        >
          Back
        </button>
        <button
          type="button"
          onClick={handlePreview}
          className={BTN_PRIMARY}
          disabled={!mappingIsValid || previewLoading}
        >
          {previewLoading ? "Validating..." : "Continue"}
        </button>
      </div>
    </div>
  );

  const renderStepThree = (
    <div id="ti-step-3" className={getSectionClassName("ti-step-3", "space-y-6")}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["Creates", summaryCounts.creates],
          ["Updates", summaryCounts.updates],
          ["Skips", summaryCounts.skips],
          ["Errors", summaryCounts.errors],
        ].map(([label, value]) => (
          <div
            key={label}
            className="rounded-2xl border border-slate-200 bg-white p-4"
          >
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {label}
            </div>
            <div className="mt-1 text-lg font-semibold text-slate-900">
              {value ?? 0}
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        {renderPagination(previewPage, pageInfo.total, setPreviewPage)}
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="min-w-full">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">Ticket #</th>
                <th className="px-3 py-2">Ticket Date</th>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2">Items</th>
                <th className="px-3 py-2">Total</th>
                <th className="px-3 py-2">
                  <span className="inline-flex items-center gap-2">
                    Action
                    <InfoTooltip text={TOOLTIPS.action} label="Action" />
                  </span>
                </th>
                <th className="px-3 py-2">
                  <span className="inline-flex items-center gap-2">
                    Issues
                    <InfoTooltip text={TOOLTIPS.issues} label="Issues" />
                  </span>
                </th>
                <th className="px-3 py-2">Details</th>
              </tr>
            </thead>
            <tbody className="text-sm md:text-base text-slate-800">
              {pageInfo.rows.map((group) => {
                const groupKey = group.group_key || group.group_id;
                const override = groupOverrides[groupKey];
                const action =
                  (override?.action || group.proposed_action || "CREATE").toUpperCase();
                const hasReview = action === "REVIEW";
                const hasError = action === "ERROR";
                const options = allowUpdates
                  ? ["CREATE", "UPDATE", "SKIP"]
                  : ["CREATE", "SKIP"];
                const isExpanded = Boolean(expandedGroups[groupKey]);
                return (
                  <Fragment key={groupKey}>
                    <tr
                      key={groupKey}
                      className="border-t border-slate-100 hover:bg-slate-50"
                    >
                      <td className="px-3 py-2 font-semibold text-slate-900">
                        {group.external_ticket_number || "-"}
                      </td>
                      <td className="px-3 py-2">
                        {group.ticket_date
                          ? formatDateOnlyYYYYMMDD(group.ticket_date)
                          : "-"}
                      </td>
                      <td className="px-3 py-2">{group.client_label || "-"}</td>
                      <td className="px-3 py-2">{group.items_count ?? 0}</td>
                      <td className="px-3 py-2">
                        ${group.totals?.grand_total ?? 0}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                              ACTION_STYLES[action] || "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {action}
                          </span>
                          <select
                            value={hasReview ? "REVIEW" : action}
                            onChange={(event) =>
                              handleOverrideAction(groupKey, event.target.value)
                            }
                            className={FIELD_CLASS}
                          >
                            {hasReview && (
                              <option value="REVIEW" disabled>
                                Review
                              </option>
                            )}
                            {hasError && (
                              <option value="ERROR" disabled>
                                Error
                              </option>
                            )}
                            {options.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-2">
                          {(group.errors || []).map((issue, idx) => {
                            const code = issueCode(issue);
                            return (
                              <span
                                key={`${groupKey}-err-${idx}`}
                                className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700"
                              >
                                {ISSUE_LABELS[code] || code}
                              </span>
                            );
                          })}
                          {(group.warnings || []).map((issue, idx) => {
                            const code = issueCode(issue);
                            return (
                              <span
                                key={`${groupKey}-warn-${idx}`}
                                className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700"
                              >
                                {ISSUE_LABELS[code] || code}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedGroups((prev) => ({
                              ...prev,
                              [groupKey]: !prev[groupKey],
                            }))
                          }
                          className="text-slate-700 underline"
                        >
                          {isExpanded ? "Hide" : "View"}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-t border-slate-100 bg-slate-50">
                        <td colSpan={8} className="px-4 py-3 text-sm text-slate-700">
                          <div className="grid gap-3 md:grid-cols-2">
                            <div>
                              <div className="text-xs font-semibold uppercase text-slate-500">
                                Totals
                              </div>
                              <div className="mt-1 text-sm text-slate-700">
                                Subtotal: ${group.totals?.sub_total ?? 0}
                                <br />
                                Discounts: ${group.totals?.discount_total ?? 0}
                                <br />
                                Tax: ${group.totals?.tax_total ?? 0}
                                <br />
                                Grand total: ${group.totals?.grand_total ?? 0}
                                <br />
                                Paid: ${group.totals?.amount_paid ?? 0}
                                <br />
                                Tips: ${group.totals?.tips_total ?? 0}
                                <br />
                                Balance due: ${group.totals?.balance_due ?? 0}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs font-semibold uppercase text-slate-500">
                                Sample Line Items
                              </div>
                              {(group.sample_items || []).length === 0 ? (
                                <div className="mt-1 text-sm text-slate-500">
                                  No line items available.
                                </div>
                              ) : (
                                <div className="mt-1 space-y-2">
                                  {group.sample_items.map((item, idx) => (
                                    <div
                                      key={`${groupKey}-item-${idx}`}
                                      className="rounded-lg border border-slate-200 bg-white px-3 py-2"
                                    >
                                      <div className="font-semibold text-slate-900">
                                        {item.service_name || "Service item"}
                                      </div>
                                      <div className="text-xs text-slate-600">
                                        {item.staff_name ? `Staff: ${item.staff_name} - ` : ""}
                                        Qty {item.quantity} - ${item.unit_price} each
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {pageInfo.rows.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-3 py-6 text-center text-sm md:text-base text-slate-500"
                  >
                    No preview groups available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="text-xs md:text-sm text-slate-500">
          Showing {pageInfo.startIndex} to {pageInfo.endIndex} of {pageInfo.total}{" "}
          entries
        </div>
      </div>

      {(preview?.new_services || []).length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
          <div className="text-sm md:text-base font-semibold text-slate-900">
            Services to be created (from ticket history)
          </div>
          <div className="overflow-hidden rounded-2xl border border-slate-200">
            <table className="min-w-full">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Base Price</th>
                  <th className="px-3 py-2">Duration</th>
                  <th className="px-3 py-2">Description</th>
                  <th className="px-3 py-2">Flags</th>
                  <th className="px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody className="text-sm md:text-base text-slate-800">
                {preview.new_services.map((service) => {
                  const override = serviceOverridesByTempKey[service.temp_key] || {};
                  const action = override.action || "CREATE";
                  return (
                    <tr
                      key={service.temp_key}
                      className="border-t border-slate-100"
                    >
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={override.name ?? service.name ?? ""}
                          onChange={(event) =>
                            handleServiceOverrideChange(
                              service.temp_key,
                              "name",
                              event.target.value
                            )
                          }
                          className={FIELD_CLASS}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          step="0.01"
                          value={
                            override.base_price ?? service.base_price ?? ""
                          }
                          onChange={(event) =>
                            handleServiceOverrideChange(
                              service.temp_key,
                              "base_price",
                              event.target.value
                            )
                          }
                          className={FIELD_CLASS}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={
                            override.duration_minutes ??
                            service.duration_minutes ??
                            ""
                          }
                          onChange={(event) =>
                            handleServiceOverrideChange(
                              service.temp_key,
                              "duration_minutes",
                              event.target.value
                            )
                          }
                          className={FIELD_CLASS}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={override.description ?? service.description ?? ""}
                          onChange={(event) =>
                            handleServiceOverrideChange(
                              service.temp_key,
                              "description",
                              event.target.value
                            )
                          }
                          className={FIELD_CLASS}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-2">
                          {service.inferred_base_price && (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                              Inferred price
                            </span>
                          )}
                          {service.needs_review && (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                              Needs review
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={action}
                          onChange={(event) =>
                            handleServiceOverrideChange(
                              service.temp_key,
                              "action",
                              event.target.value
                            )
                          }
                          className={FIELD_CLASS}
                        >
                          <option value="CREATE">Create</option>
                          <option value="SKIP">Skip</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(preview?.new_staff || []).length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
          <div className="text-sm md:text-base font-semibold text-slate-900">
            Staff to be created (from ticket history)
          </div>
          <div className="overflow-hidden rounded-2xl border border-slate-200">
            <table className="min-w-full">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2">Display Name</th>
                  <th className="px-3 py-2">Phone</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Flags</th>
                  <th className="px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody className="text-sm md:text-base text-slate-800">
                {preview.new_staff.map((staff) => {
                  const override = staffOverridesByTempKey[staff.temp_key] || {};
                  const action = override.action || "CREATE";
                  return (
                    <tr
                      key={staff.temp_key}
                      className="border-t border-slate-100"
                    >
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={override.display_name ?? staff.display_name ?? ""}
                          onChange={(event) =>
                            handleStaffOverrideChange(
                              staff.temp_key,
                              "display_name",
                              event.target.value
                            )
                          }
                          className={FIELD_CLASS}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={override.phone ?? staff.phone ?? ""}
                          onChange={(event) =>
                            handleStaffOverrideChange(
                              staff.temp_key,
                              "phone",
                              event.target.value
                            )
                          }
                          className={FIELD_CLASS}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={override.email ?? staff.email ?? ""}
                          onChange={(event) =>
                            handleStaffOverrideChange(
                              staff.temp_key,
                              "email",
                              event.target.value
                            )
                          }
                          className={FIELD_CLASS}
                        />
                      </td>
                      <td className="px-3 py-2">
                        {staff.needs_review && (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                            Needs review
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={action}
                          onChange={(event) =>
                            handleStaffOverrideChange(
                              staff.temp_key,
                              "action",
                              event.target.value
                            )
                          }
                          className={FIELD_CLASS}
                        >
                          <option value="CREATE">Create</option>
                          <option value="SKIP">Skip</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {unresolvedPreviewErrors && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
          Resolve groups with errors before continuing (set them to Skip).
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setStep(2)}
          className={BTN_OUTLINE}
        >
          Back
        </button>
        <button
          type="button"
          onClick={() => setStep(4)}
          className={BTN_PRIMARY}
          disabled={unresolvedPreviewErrors}
        >
          Continue
        </button>
      </div>
    </div>
  );

  const renderStepFour = (
    <div id="ti-step-4" className={getSectionClassName("ti-step-4", "space-y-5")}>
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Ready to import
        </div>
        <div className="mt-1 text-sm md:text-base text-slate-700">
          {summaryCounts.creates} creates, {summaryCounts.updates} updates,{" "}
          {summaryCounts.skips} skips.
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setStep(3)}
          className={BTN_OUTLINE}
          disabled={commitLoading}
        >
          Back
        </button>
        <div className="inline-flex items-center gap-2">
          <button
            type="button"
            onClick={handleCommit}
            className={BTN_PRIMARY}
            disabled={commitLoading}
          >
            {commitLoading ? "Importing..." : "Commit Import"}
          </button>
          <InfoTooltip text={TOOLTIPS.commit} label="Commit Import" />
        </div>
      </div>

      {commitResult && (
        <div className="space-y-3">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm md:text-base text-emerald-700">
            Import complete. {commitResult.created_count} created,{" "}
            {commitResult.updated_count} updated, {commitResult.skipped_count} skipped,{" "}
            {commitResult.failed_count} failed.
            <div className="mt-2 text-sm text-emerald-700">
              Services created: {commitResult.services_created ?? 0} · Needs review:{" "}
              {commitResult.services_needs_review ?? 0}
            </div>
            <div className="text-sm text-emerald-700">
              Staff created: {commitResult.staff_created ?? 0} · Needs review:{" "}
              {commitResult.staff_needs_review ?? 0}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate("/tickets/imports")}
              className={BTN_OUTLINE}
            >
              View Import History
            </button>
            <button
              type="button"
              onClick={() => setRollbackModalOpen(true)}
              className={BTN_DANGER}
              disabled={rollbackLoading || Boolean(rollbackReport)}
            >
              {rollbackLoading ? "Rolling back..." : "Undo Import"}
            </button>
          </div>

          {(commitResult.failures || []).length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2">
              <div className="text-sm font-semibold text-slate-900">
                Sample Failures
              </div>
              <div className="space-y-2">
                {commitResult.failures.slice(0, 5).map((failure, idx) => (
                  <div
                    key={`${failure.group_key || failure.group_id}-${idx}`}
                    className="text-sm text-slate-700"
                  >
                    {failure.group_key || failure.group_id}:{" "}
                    {(failure.errors || []).join(", ")}
                  </div>
                ))}
              </div>
            </div>
          )}

          {rollbackReport && (
            <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
              <div className="text-sm md:text-base text-slate-700">
                Rollback complete. {rollbackReport.deleted_created_tickets} created
                tickets deleted, {rollbackReport.reverted_updated_tickets} updates
                reverted, {rollbackReport.skipped} skipped.
              </div>
              <div className="text-sm text-slate-600">
                Services deleted: {rollbackReport.services_deleted ?? 0} · Kept due to
                usage: {(rollbackReport.services_kept_due_to_usage || []).length}
              </div>
              <div className="text-sm text-slate-600">
                Staff deleted: {rollbackReport.staff_deleted ?? 0} · Kept due to usage:{" "}
                {(rollbackReport.staff_kept_due_to_usage || []).length}
              </div>
              {(rollbackReport.skipped_details || []).length > 0 && (
                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <table className="min-w-full">
                    <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2">Ticket ID</th>
                        <th className="px-3 py-2">Action</th>
                        <th className="px-3 py-2">Reasons</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm md:text-base text-slate-700">
                      {rollbackReport.skipped_details.map((entry, idx) => (
                        <tr
                          key={`${entry.ticket_id}-${idx}`}
                          className="border-t border-slate-100 hover:bg-slate-50"
                        >
                          <td className="px-3 py-2">{entry.ticket_id}</td>
                          <td className="px-3 py-2 uppercase text-xs font-semibold text-slate-500">
                            {entry.action}
                          </td>
                          <td className="px-3 py-2">
                            {(entry.reasons || []).join(", ")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {typeof onClose === "function" && (
            <button type="button" onClick={onClose} className={BTN_OUTLINE}>
              Done
            </button>
          )}
        </div>
      )}
    </div>
  );

  const wizardBody = (
    <div className={`space-y-6 ${MODAL_TEXT}`}>
      <div id="ti-overview">
        <ReportHelpCard
          title="Help Guide: Import Tickets"
          subtitle="Follow these steps to upload, validate, and commit ticket imports safely."
          sections={HELP_SECTIONS}
          troubleshooting={TROUBLESHOOTING_ITEMS}
          onJumpToSection={jumpToSectionWithHighlight}
        />
      </div>
      {stepHeader}
      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
          {typeof error === "string" ? error : "Something went wrong."}
        </div>
      )}
      {step === 1 && renderStepOne}
      {step === 2 && renderStepTwo}
      {step === 3 && renderStepThree}
      {step === 4 && renderStepFour}
    </div>
  );

  const rollbackModal = (
    <SBINModal
      isOpen={rollbackModalOpen}
      onClose={() => setRollbackModalOpen(false)}
      title="Undo Import"
      size="lg"
      fitContent
    >
      <div className="space-y-4">
        <p className="text-sm md:text-base text-slate-700">
          This will roll back the import. Created tickets are deleted only if they
          have no refunds, appointment links, or updates after the import. Updated
          tickets revert only if they have not been modified since the import.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setRollbackModalOpen(false)}
            className={BTN_OUTLINE}
            disabled={rollbackLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleRollback}
            className={BTN_DANGER}
            disabled={rollbackLoading}
          >
            {rollbackLoading ? "Rolling back..." : "Confirm Rollback"}
          </button>
        </div>
      </div>
    </SBINModal>
  );

  if (!embedded) {
    return (
      <>
        {wizardBody}
        {rollbackModal}
      </>
    );
  }

  return (
    <>
      <div
        className={`rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden ${MODAL_TEXT}`}
      >
        <div className="bg-rose-500 px-6 py-4">
          <h2 className="text-xl md:text-2xl font-semibold text-white">
            Tickets
          </h2>
        </div>
        <div className="px-6 py-6 space-y-6">{wizardBody}</div>
      </div>
      {rollbackModal}
    </>
  );
}

